import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { Observable, ReplaySubject, from, map, of, switchMap } from 'rxjs';
import { watchCookie$ } from 'src/app/core/cookie-utils';
import { v5 as uuidv5 } from 'uuid';
import { environment } from '../../../environments/environment';
import { getIsNotNullable } from '../nullable-utils';

export interface IxUser {
  email?: string;
  displayName: string;
  id: string;
  roles: unknown;
}

export const getIsValidUser = (user: IxUser): boolean => user.email !== '';

@Injectable()
export class AuthService {
  readonly #user$ = new ReplaySubject<IxUser>(1);
  readonly user$: Observable<IxUser> = this.#user$.asObservable();

  readonly #oidcSettings: UserManagerSettings = {
    authority: environment.OIDC_SETTINGS.authority,
    client_id: environment.OIDC_SETTINGS.clientId,
    redirect_uri: environment.OIDC_SETTINGS.redirectUri,
    scope: environment.OIDC_SETTINGS.scope,
    silent_redirect_uri: environment.OIDC_SETTINGS.silentRedirectUri,
    post_logout_redirect_uri: environment.OIDC_SETTINGS.postLogoutRedirectUri,
    response_type: 'code',
    automaticSilentRenew: true,
  };

  readonly #manager = new UserManager(this.#oidcSettings);

  constructor() {
    this.#setUserData();

    this.#manager.events.addUserLoaded(() => this.#setUserData());
    this.#manager.events.addUserUnloaded(() => this.#clearUserData());
    this.#manager.events.addSilentRenewError(() => this.#manager.removeUser());
  }

  redirectLogin(): Promise<void> {
    return this.#manager.signinRedirect({
      redirectMethod: 'replace',
      state: window.location.pathname + window.location.search,
    });
  }

  logout(): Promise<void> {
    return this.#manager.signoutRedirect();
  }

  signinCallback(): Promise<User | void> {
    return this.#manager.signinCallback();
  }

  getUserState(): Observable<boolean> {
    return from(this.#manager.getUser()).pipe(
      map(user => !!(getIsNotNullable(user) && !user.expired && !!user.access_token)),
    );
  }

  hasTenantAdminRole(): Observable<boolean> {
    const adminOverride$ = watchCookie$('tenantadmin');

    return adminOverride$.pipe(
      switchMap(adminOverride =>
        getIsNotNullable(adminOverride)
          ? of(adminOverride === 'true')
          : this.user$.pipe(map(user => getIsStringArray(user.roles) && user.roles.includes('tenant.admin'))),
      ),
    );
  }

  getAccessToken(): Observable<string | undefined> {
    return from(this.#manager.getUser()).pipe(
      switchMap(user => {
        if (getIsNotNullable(user) && !user.expired && !!user.access_token) {
          return of(user.access_token);
        } else {
          return from(this.#manager.signinSilent()).pipe(map(user => user?.access_token));
        }
      }),
    );
  }

  #setUserData() {
    this.#manager.getUser().then(user => {
      if (user) {
        const email = extractEmailWithFallback(user);

        this.#user$.next({
          email,
          displayName: user.profile.name || 'Unknown',
          id: uuidv5(`${email}`, uuidv5.DNS),
          roles: user.profile.roles || [],
        });
      }
    });
  }

  #clearUserData() {
    this.#user$.next({
      email: '',
      displayName: '',
      id: '',
      roles: [],
    });
  }
}

function getIsStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

export function extractEmailWithFallback(oidcUser: User): string {
  const email = oidcUser.profile.email ?? getUpnFromAccessToken(oidcUser.access_token);

  if (!email) {
    throw new Error('No email found in profile or UPN claim');
  }

  return parseExternalEmail(email);
}

function getUpnFromAccessToken(accessToken: string): string | undefined {
  try {
    const decoded = jwtDecode<{ upn?: string }>(accessToken);
    return decoded.upn;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return undefined;
  }
}

export function parseExternalEmail(email: string): string {
  const extIndex = email.indexOf('#EXT#');
  if (extIndex === -1) {
    return email;
  }

  // Handle external address format:
  // andreas.svendsen_aize.io#EXT#@akersolutions.onmicrosoft.com
  const baseEmail = email.substring(0, extIndex);
  return baseEmail.replace(/(.+)_(.+)$/, '$1@$2');
}
